import Intercom, {
  show,
  showArticle,
  shutdown,
  update,
} from '@intercom/messenger-js-sdk'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useNotify } from '@/utils/useNotify'

import { useCoreStore } from '../store/useCoreStore'

export const useIntercomStore = defineStore('intercom', () => {
  const notify = useNotify()
  const { t } = useI18n()
  const intercomInitialized = ref(false)
  const authStore = useAuthStore()
  const coreStore = useCoreStore()

  /**
   * Resolve the brand from the core store, falling back to the persisted brand
   * session: eager init (post-login, session restore) runs before `loadBrand`
   * populates `activeBrandId`, but `brandSession` is already hydrated, so
   * external customers still send `X-Active-Brand-ID`. A `pb:super` internal
   * user has no brand on either, so the header is omitted by design. The
   * auth-service wildcard is also omitted because Olympus rejects it as an
   * explicit brand header.
   */
  async function fetchIntercomToken(): Promise<string> {
    const brandId = coreStore.activeBrandId ?? authStore.brandSession?.brandId
    const headers =
      brandId && brandId !== '*'
        ? { headers: { 'X-Active-Brand-ID': brandId } }
        : {}

    const response =
      await OlympusClient.auth.v2.hermes.getIntercomToken(headers)
    return response.token
  }

  const initializeIntercom = async () => {
    if (intercomInitialized.value) return
    if (!authStore.isAuthenticated) return
    if (
      import.meta.env.MODE !== 'production' &&
      import.meta.env.VITE_FORCE_ENABLE_INTERCOM !== 'true'
    )
      return

    try {
      intercomInitialized.value = true

      const token = await fetchIntercomToken()

      Intercom({
        app_id: import.meta.env.VITE_INTERCOM_APP_ID,
        current_location_id: coreStore.activeLocationId ?? undefined,
        hide_default_launcher: true,
        intercom_user_jwt: token,
      })
    } catch (e) {
      logError(e)
      notify(t('core.errors.intercom-load'), 'error')
      intercomInitialized.value = false
    }
  }

  const updateIntercomAttributes = async () => {
    if (!intercomInitialized.value) {
      await initializeIntercom()
      return
    }

    try {
      const token = await fetchIntercomToken()
      update({
        current_location_id: coreStore.activeLocationId ?? undefined,
        intercom_user_jwt: token,
      })
    } catch (e) {
      logError(e)
    }
  }

  const shutdownIntercom = () => {
    if (intercomInitialized.value) {
      try {
        shutdown()
      } catch (e) {
        // Silently suppress Intercom shutdown errors (e.g., during logout)
        // No need to notify users about support chat cleanup failures
        logError(e)
      } finally {
        intercomInitialized.value = false
      }
    }
  }

  const showIntercom = async () => {
    if (!intercomInitialized.value) {
      await initializeIntercom()
    }

    if (intercomInitialized.value) {
      show()
    }
  }

  const showIntercomArticle = async (articleId: string): Promise<boolean> => {
    if (!intercomInitialized.value) {
      await initializeIntercom()
    }

    if (intercomInitialized.value) {
      showArticle(articleId)
      return true
    }

    return false
  }

  return {
    initializeIntercom,
    intercomInitialized,
    showIntercom,
    showIntercomArticle,
    shutdownIntercom,
    updateIntercomAttributes,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useIntercomStore, import.meta.hot))
}
